import { useCallback } from 'react';

import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { Metric } from '@atlassian/browser-metrics/metric';
import { BMEventsType } from '@atlassian/browser-metrics/types';
import { isRemixGeneratedFlow } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { getConfluenceContentIdFromUrl } from '@atlassian/conversation-assistant-action-helpers/utils/content-url';
import { getContentTypeFromActionKey } from '@atlassian/conversation-assistant-action-helpers/utils/get-content-type-from-action-key';
import { isJiraCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jira-actions';
import { isJsmCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jsm-actions';
import type { LinkNavigationInterceptor } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/LinkNavigationInterceptor';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';

import { useOpenStagedContentPreview } from '../open-staged-content-preview/use-open-staged-content-preview';
import {
	findStagedContentLinkMatch,
	type StagedContentLinkMatch,
} from './resolve-staged-content-link-click';

const stagedLinkMatchMetric = new Metric({
	key: 'ai-mate.staged-content-link-match',
	type: BMEventsType.INLINE_RESULT,
});

const getMessageCountBucket = (count: number): string => {
	if (count <= 50) {
		return 'upTo50';
	}
	if (count <= 200) {
		return 'upTo200';
	}
	if (count <= 1000) {
		return 'upTo1000';
	}
	return 'over1000';
};

export type UseStagedContentLinkInterceptorParams = {
	conversationId: string | undefined;
	productKey: string | undefined;
	stagingAreaEnabled: boolean | undefined;
	fullScreen: boolean | undefined;
};

/**
 * Returns a stable `LinkNavigationInterceptor` for Rovo chat. If a clicked link points at an
 * unfinalized staged item, it re-points the staging-area preview to that item (or opens the staging
 * area on it) and returns `true`. Otherwise it returns `false` and the caller navigates as usual.
 *
 * The experiment is checked first so that nothing else runs when it is off. The tradeoff is that
 * exposure fires on every link click, not only on clicks that match a staged item.
 *
 * `productKey`, `stagingAreaEnabled` and `fullScreen` are passed in because this package has no
 * dependency on conversation-assistant's config hooks. Store state is read through getters rather
 * than subscriptions, so a selection change doesn't re-render every message.
 */
export const useStagedContentLinkInterceptor = ({
	conversationId,
	productKey,
	stagingAreaEnabled,
	fullScreen,
}: UseStagedContentLinkInterceptorParams): LinkNavigationInterceptor => {
	const { getActiveContext } = useChatContextStoreActions();
	const { getMessages } = useChatMessagesActions();
	const {
		setInvocationIdSelectedForPreview,
		getMessageIdSelectedForPreview,
		getInvocationIdSelectedForPreview,
	} = useStagingAreaActions();
	const openStagedContentPreview = useOpenStagedContentPreview();

	const openPreview = useCallback(
		(stagedContentMatch: StagedContentLinkMatch, source: 'hyperlink' | 'smartLink') => {
			const isMauiPreview =
				isRemixGeneratedFlow(stagedContentMatch.action) &&
				expValEquals('cc-maui-experiment', 'isEnabled', true);

			openStagedContentPreview({
				conversationId,
				messageId: stagedContentMatch.messageId,
				invocationId: stagedContentMatch.invocationId,
				contentId: stagedContentMatch.contentId,
				// `stagedContentMatch.contentType` uses a different vocabulary than the preview
				// card's `ContentType` enum, so derive it from the action key as the card does.
				contentType: getContentTypeFromActionKey(stagedContentMatch.action.key),
				actionKey: stagedContentMatch.action.key,
				isMauiPreview,
				isJiraCreateAction: isJiraCreatableAction(stagedContentMatch.action),
				isJsmCreateAction: isJsmCreatableAction(stagedContentMatch.action),
				stagingAreaEnabled,
				fullScreen,
				product: productKey,
				source,
			});
		},
		[openStagedContentPreview, conversationId, stagingAreaEnabled, fullScreen, productKey],
	);

	return useCallback(
		(
			url: string,
			options?: { modifierKey: boolean; source?: 'hyperlink' | 'smartLink' },
		): boolean => {
			if (!expVal('rovo_chat_staging_area_sibling_links', 'isEnabled', false)) {
				return false;
			}

			// Modifier-clicks keep the browser's new-tab behaviour.
			if (options?.modifierKey) {
				return false;
			}

			const messages = getMessages(conversationId ?? '');
			// Only links with a content id trigger a conversation scan, so only those are timed.
			const isTimed = Boolean(getConfluenceContentIdFromUrl(url));

			if (isTimed) {
				stagedLinkMatchMetric.start({ startTime: performance.now() });
			}
			const stagedContentMatch = findStagedContentLinkMatch(url, messages);
			if (isTimed) {
				stagedLinkMatchMetric.stop({
					customData: {
						outcome: stagedContentMatch ? 'matched' : 'noMatch',
						messageCountBucket: getMessageCountBucket(messages.length),
					},
				});
			}

			if (!stagedContentMatch) {
				return false;
			}

			const isStagingAreaOpen =
				getMessageIdSelectedForPreview() !== null || getInvocationIdSelectedForPreview() !== null;

			if (getActiveContext() === 'staging-area' && isStagingAreaOpen) {
				// Already showing the staging area: only the selection needs to change.
				if (stagedContentMatch.invocationId !== getInvocationIdSelectedForPreview()) {
					setInvocationIdSelectedForPreview(stagedContentMatch.invocationId);
				}
				return true;
			}

			// Otherwise (including a stale selection from a modal closed outside the store), open it
			// fresh, as the Preview button would.
			openPreview(stagedContentMatch, options?.source ?? 'hyperlink');
			return true;
		},
		[
			getActiveContext,
			getMessageIdSelectedForPreview,
			getInvocationIdSelectedForPreview,
			getMessages,
			conversationId,
			setInvocationIdSelectedForPreview,
			openPreview,
		],
	);
};
